import { Button, Collapse, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { IProduct } from "types/productManagementTypes";
import ConfigJsonView from "./ConfigJsonView";

export type ProductFetchStatus = "loading" | "error" | "loaded";

// `product.states` is the snapshot the headset reports on connect
// (GetAllStates on the device): actual system values plus the playerPrefs
// and the managementConfig stored on the device. Toggle values arrive as
// "0"/"1" strings from the PICO enterprise API.

export const onOff = (value: unknown): string => {
  if (value === undefined || value === null || value === "" || value === "unknown") return "-";
  if (value === "1" || value === 1 || value === true) return "On";
  if (value === "0" || value === 0 || value === false) return "Off";
  return String(value);
};

export const plain = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "-";
  return Array.isArray(value) ? value.join(", ") : String(value);
};

// GetSleepDelay/GetScreenOffDelay return C# enums, so the device reports the
// enum NAME ("NEVER", "SIX_HUNDRED", ...). Normalize to the numeric seconds
// used by the config fields, with never as -1.
export const TIMEOUT_ENUM_SECONDS: Record<string, number> = {
  NEVER: -1,
  THREE: 3,
  TEN: 10,
  FIFTEEN: 15,
  THIRTY: 30,
  SIXTY: 60,
  THREE_HUNDRED: 300,
  SIX_HUNDRED: 600,
  ONE_THOUSAND_AND_EIGHT_HUNDRED: 1800,
};

export const timeoutSeconds = (value: unknown): string => {
  if (value === undefined || value === null) return "-";
  const raw = String(value).trim();
  if (raw === "") return "-";
  const seconds = Object.prototype.hasOwnProperty.call(TIMEOUT_ENUM_SECONDS, raw)
    ? TIMEOUT_ENUM_SECONDS[raw]
    : Number(raw);
  if (!Number.isFinite(seconds)) return raw;
  return seconds === -1 ? "-1 (Never)" : String(seconds);
};

export const buildStateRows = (product: IProduct | null): { label: string; value: string }[] => {
  const states = (product?.states ?? {}) as Record<string, unknown>;
  const playerPrefs = (states.playerPrefs ?? {}) as Record<string, unknown>;
  return [
    { label: "Serial", value: plain(states.serialNo) },
    { label: "Model", value: plain(states.model) },
    { label: "PUI", value: plain(states.pui ?? product?.puiVersion) },
    { label: "Wi-Fi SSID", value: plain(states.wifi_ssid) },
    { label: "Device IP", value: plain(states.deviceIp) },
    { label: "Brightness", value: plain(states.brightness) },
    { label: "Sleep delay", value: timeoutSeconds(states.sleepDelay) },
    { label: "Screen-off delay", value: timeoutSeconds(states.screenOffDelay) },
    { label: "USB debugging", value: onOff(states.usbDebugging) },
    { label: "System update", value: onOff(states.systemUpdate) },
    { label: "Wi-Fi always on", value: onOff(states.wifiAlwaysOn) },
    { label: "Play boundary", value: onOff(states.playBoundary) },
    { label: "App as home", value: plain(states.appAsHome) },
    { label: "Logcat enabled", value: plain(playerPrefs.enableLogger) },
    { label: "Local log enabled", value: plain(playerPrefs.enableFileLogger) },
    { label: "Grafana log enabled", value: plain(playerPrefs.enableLokiLogger) },
    { label: "Log mode", value: plain(playerPrefs.logMode) },
    { label: "Config key", value: plain(playerPrefs.configKey) },
    { label: "Keys disabled on test", value: plain(playerPrefs.disableKeyConfigOnTest) },
  ];
};

interface DeviceStatesPanelProps {
  product: IProduct | null;
  status: ProductFetchStatus;
}

// The product is fetched once by the parent modal and shared with the other
// panels, so opening a config does a single getProduct request.
const DeviceStatesPanel: React.FC<DeviceStatesPanelProps> = ({ product, status }) => {
  const [showStoredConfig, setShowStoredConfig] = useState(false);

  useEffect(() => {
    setShowStoredConfig(false);
  }, [product]);

  const states = (product?.states ?? {}) as Record<string, unknown>;
  const hasStates = Object.keys(states).length > 0;
  const storedConfig = states.managementConfig;

  return (
    <Paper withBorder radius="sm" p="md">
      <Stack gap="xs">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Reported device state
          </Text>
          <Text size="xs" c="dimmed">
            What the device itself reported when it last connected
            {product?.lastAccess
              ? ` (last access ${new Date(product.lastAccess).toLocaleString()})`
              : ""}
            .
          </Text>
        </Stack>
        {status === "loading" && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Loading device state…
            </Text>
          </Group>
        )}
        {status === "error" && (
          <Text size="sm" c="red">
            Failed to load the device state
          </Text>
        )}
        {status === "loaded" && !hasStates && (
          <Text size="sm" c="dimmed">
            The device has not reported its state yet.
          </Text>
        )}
        {status === "loaded" && hasStates && (
          <Stack gap="xs">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              {buildStateRows(product).map(({ label, value }) => (
                <Group key={label} gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed" w={150} style={{ flexShrink: 0 }}>
                    {label}
                  </Text>
                  <Text size="sm" style={{ wordBreak: "break-word" }}>
                    {value}
                  </Text>
                </Group>
              ))}
            </SimpleGrid>
            {!!storedConfig && (
              <>
                <div>
                  <Button
                    size="compact-xs"
                    variant="default"
                    onClick={() => setShowStoredConfig((s) => !s)}
                  >
                    {showStoredConfig
                      ? "Hide config stored on device"
                      : "Show config stored on device"}
                  </Button>
                </div>
                <Collapse in={showStoredConfig}>
                  <ConfigJsonView value={storedConfig} />
                </Collapse>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default DeviceStatesPanel;
